import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Message, SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { UserStore } from '../../../../stores/user/user.state';
import { RoutingService } from '../../../../services/routing.service';
import { UserService } from '../../../../services/user/user.service';
// NG21-MIGRATION(stufe-0): Server-Import im Browser-Code; express fällt mit SSR weg -> Import entfernen (§6.4)
import { response } from 'express';
import { Subscription } from 'rxjs';
import { AlertComponent } from '../../../atoms/alert/alert.component';

@Component({
  selector: 'app-initial-password',
  standalone: true,
  imports: [
    ButtonModule,
    CardModule,
    InputTextModule,
    MessagesModule,
    NgIf,
    PaginatorModule,
    ReactiveFormsModule,
    SharedModule,
    AlertComponent,
  ],
  templateUrl: './initial-password.component.html',
  styleUrl: './initial-password.component.scss',
})
export class InitialPasswordComponent implements OnInit {
  isReset = false;
  setPasswordForm: FormGroup = new FormGroup({});
  submitted = false;
  error: Message = {
    severity: 'error',
    summary: 'fehlgeschlagen',
    detail: '',
  };
  loading = false;
  messages: Message[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private routingService: RoutingService,
    private userService: UserService
  ) {}

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * Initializes the `setPasswordForm` with form controls for `password` and `passwordRepeat`
   * and applies required validation rules to both controls.
   *
   * @return {void} This method does not return any value*/
  ngOnInit(): void {
    this.setPasswordForm = this.formBuilder.group(
      {
        password: ['', Validators.required],
        passwordRepeat: ['', Validators.required],
      },
      { validators: this.passwordMatchValidator() }
    );
  }

  /**
   * A custom validator function that checks whether the 'password' and 'passwordRepeat' fields
   * in the form have matching values. If the values do not match, it returns a validation error.
   *
   * @return {ValidatorFn} A validator function that returns a validation error object with the 'missMatch' key
   * if the values do not match, or null if they match or either field is missing.
   */
  passwordMatchValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.get('password');
      const passwordRepeat = control.get('passwordRepeat');

      if (!password || !passwordRepeat) {
        return null;
      }

      return password.value === passwordRepeat.value
        ? null
        : { missMatch: true };
    };
  }

  /**
   * Adds a mismatch error to the `messages` array, indicating that passwords do not match.
   *
   * @return {void} This method does not return a value.
   */
  addMissMatchError() {
    this.messages = [
      {
        severity: 'error',
        summary: 'Passwörter stimmen nicht überein',
        detail: '',
      },
    ];
  }

  /**
   * Checks if the passwords provided in the form match.
   *
   * @return {boolean} Returns true if the passwords match, otherwise false.
   */
  get passwordsMatch(): boolean {
    return this.setPasswordForm.getError('missMatch');
  }

  /**
   * Handles the form submission process for setting a new password.
   * This method validates the form, retrieves necessary parameters from the route,
   * and calls the appropriate password reset service based on the obtained parameters.
   *
   * @return {undefined|Subscription} Returns void if the form is invalid or parameters are missing.
   *         Returns an Observable if the password reset request is successfully triggered.
   */
  onSubmit(): Subscription | undefined {
    this.submitted = true;
    const { password } = this.setPasswordForm.value;

    if (this.setPasswordForm.invalid) {
      if (this.passwordsMatch) this.addMissMatchError();
      console.log(this.messages);
      console.log(this.setPasswordForm);
      return;
    }

    const token = this.routingService.getParameterFromRoute('token');
    const userId = this.routingService.getIdFromRoute();

    if (token) {
      this.userService
        .resetPasswordByToken(password, token)
        .subscribe(response => {
          this.routingService.redirectToRoute(['/login']);
        });
    } else if (userId) {
      return this.userService
        .resetPassword(password, userId)
        .subscribe(response => {
          this.routingService.redirectToRoute(['/login']);
        });
    }
    return;
  }
}
